import { useState, useRef, useEffect, useCallback } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { SignaturePad } from '../SignaturePad';
import { Button } from '@/components/ui/button';
import { BrandedSpinner } from '@/components/ui/branded-spinner';
import { feeInformationDocumentText } from '@/data/documents/feeInformationDocument';
import paynovusLogo from '@/assets/paynovus-logo-horizontal.png';
import fidLogo from '@/assets/fid-common-symbol.svg';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { resolveCountryName } from '@/utils/countryResolver';
import { toast } from '@/hooks/use-toast';
import { 
  FileText, 
  ChevronRight, 
  Check, 
  ArrowLeft,
  DollarSign,
  Shield,
  Info,
  ScrollText,
  PenLine
} from 'lucide-react';

interface Document {
  id: string;
  title: string;
  icon: React.ElementType;
  required: boolean;
  viewed: boolean;
}

interface Confirmation {
  id: string;
  title: string;
  description: string;
  checked: boolean;
}

export function DocumentSigningStep() {
  const { goToNextStep, updateData, setCustomBackHandler, data } = useOnboarding();
  const isCorporate = data.accountType === 'corporate';
  const [documents, setDocuments] = useState<Document[]>([
    { id: 'terms', title: 'General Terms and Conditions - Accounts', icon: FileText, required: true, viewed: false },
    { id: 'fees', title: isCorporate ? 'Standard Fee - Business Account' : 'Standard Fee - Personal Account', icon: DollarSign, required: true, viewed: false },
    { id: 'gtc-cards', title: 'General Terms and Conditions - Cards', icon: FileText, required: true, viewed: false },
    { id: 'fee-doc', title: isCorporate ? 'Standard Fee - Business Debit Card' : 'Standard Fee - Personal Debit Card', icon: ScrollText, required: true, viewed: false },
    { id: 'info-sheet', title: 'Standardised Information Sheet', icon: Info, required: true, viewed: false },
    { id: 'privacy-policy', title: 'Privacy Policy', icon: Shield, required: true, viewed: false },
  ]);

  // Removed 'certificate' and 'immediate-service' confirmations
  const [confirmations, setConfirmations] = useState<Confirmation[]>([
    {
      id: 'accept-docs',
      title: 'Document Acceptance',
      description: "You confirm that you have read and accepted PayNovus's documents above, and that you accept signing them with electronic signature and passcode",
      checked: false,
    },
    {
      id: 'evidence',
      title: 'Document Evidence',
      description: 'You recognise the signed documents as full and sufficient evidence of their content',
      checked: false,
    },
    {
      id: 'signature-service',
      title: 'Qualified Electronic Signature Service',
      description: 'You confirm that you have read and understood the PKI Disclosure Statement, Privacy Policy and General Terms and Conditions for the Qualified Electronic Signature Service',
      checked: false,
    },
    {
      id: 'data-accuracy',
      title: 'Data Accuracy Confirmation',
      description: 'You confirm that the data issued by you are true and accurate and accept any and all liability (including criminal liability) regarding its accuracy',
      checked: false,
    },
  ]);

  const [viewingDoc, setViewingDoc] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  // Track scroll state PER DOCUMENT - each document must be scrolled independently
  const [scrolledDocs, setScrolledDocs] = useState<Set<string>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Refs for auto-scroll sections
  const firstConfirmationsRef = useRef<HTMLDivElement>(null);
  const secondConfirmationsRef = useRef<HTMLDivElement>(null);
  const signatureRef = useRef<HTMLDivElement>(null);
  
  // Signature state
  const [signature, setSignature] = useState<string | null>(null);

  const allDocsViewed = documents.every((doc) => doc.viewed);
  const firstTwoConfirmationsChecked = confirmations.slice(0, 2).every((conf) => conf.checked);
  const allConfirmationsChecked = confirmations.every((conf) => conf.checked);
  const hasSignature = signature !== null;
  
  // Can only sign if all docs viewed, all confirmations checked, and signature provided
  const canSign = allDocsViewed && allConfirmationsChecked && hasSignature;

  // Check if current document has been scrolled to bottom
  const hasScrolledCurrentDoc = viewingDoc ? scrolledDocs.has(viewingDoc) : false;

  // Track scroll height stability to prevent premature activation
  const lastScrollHeightRef = useRef<number>(0);
  const stabilityCheckCountRef = useRef<number>(0);
  const stabilityTimerRef = useRef<NodeJS.Timeout | null>(null);

  const markCurrentDocScrolled = useCallback(() => {
    if (viewingDoc) {
      setScrolledDocs(prev => new Set(prev).add(viewingDoc));
    }
  }, [viewingDoc]);

  const checkScrollPosition = useCallback((isManualScroll = false) => {
    if (contentRef.current && viewingDoc) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      
      // For manual scroll events: user must have scrolled AND reached bottom
      if (isManualScroll) {
        const isAtBottom = scrollTop > 0 && scrollTop + clientHeight >= scrollHeight - 50;
        if (isAtBottom) markCurrentDocScrolled();
        return;
      }
    }
  }, [viewingDoc, markCurrentDocScrolled]);

  // Check if content fits in viewport with stability verification
  // IMPORTANT: Be very strict - fee documents have lots of content so should always require scrolling
  const checkContentFitsViewport = useCallback(() => {
    if (!contentRef.current || !viewingDoc) return;
    
    const { scrollHeight, clientHeight } = contentRef.current;
    
    // Minimum content height required - legal documents should be at least 800px tall
    // If content is below this threshold, it's still loading and we shouldn't auto-enable
    const MIN_EXPECTED_CONTENT_HEIGHT = 800;
    
    // Check if height has stabilized (same as last check)
    if (scrollHeight === lastScrollHeightRef.current) {
      stabilityCheckCountRef.current += 1;
      
      // Require 5 consecutive stable checks (1 second total) before considering enabling
      if (stabilityCheckCountRef.current >= 5) {
        // Only auto-enable if content genuinely fits AND has loaded (above min height threshold)
        // OR if the content is very short (info boxes, etc) - but our legal docs are never that short
        const noScrollingNeeded = scrollHeight <= clientHeight + 5;
        const contentHasLoaded = scrollHeight >= MIN_EXPECTED_CONTENT_HEIGHT;
        
        // Only auto-mark if content fits AND either it's loaded OR it's genuinely tiny content
        if (noScrollingNeeded && (contentHasLoaded || scrollHeight < 200)) {
          markCurrentDocScrolled();
        }
        // Stop further checks
        if (stabilityTimerRef.current) {
          clearInterval(stabilityTimerRef.current);
          stabilityTimerRef.current = null;
        }
        return;
      }
    } else {
      // Height changed, reset stability counter
      stabilityCheckCountRef.current = 0;
      lastScrollHeightRef.current = scrollHeight;
    }
  }, [viewingDoc, markCurrentDocScrolled]);

  const handleScroll = useCallback(() => {
    checkScrollPosition(true);
  }, [checkScrollPosition]);

  useEffect(() => {
    if (viewingDoc) {
      // Reset stability tracking refs when opening a new document
      // Note: scrolledDocs tracks per-document state, so no need to reset
      lastScrollHeightRef.current = 0;
      stabilityCheckCountRef.current = 0;
      
      // Clear any existing stability timer
      if (stabilityTimerRef.current) {
        clearInterval(stabilityTimerRef.current);
        stabilityTimerRef.current = null;
      }
      
      requestAnimationFrame(() => {
        contentRef.current?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
        
        // Start stability checking after longer initial delay to ensure content has rendered
        // Wait 800ms for content to fully render before starting checks
        setTimeout(() => {
          // Check every 200ms for 5 consecutive stable heights (1 second total)
          stabilityTimerRef.current = setInterval(checkContentFitsViewport, 200);
          
          // Maximum wait time of 3 seconds
          setTimeout(() => {
            if (stabilityTimerRef.current) {
              clearInterval(stabilityTimerRef.current);
              stabilityTimerRef.current = null;
            }
          }, 3000);
        }, 800);
      });
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    
    // Cleanup on unmount or doc change
    return () => {
      if (stabilityTimerRef.current) {
        clearInterval(stabilityTimerRef.current);
        stabilityTimerRef.current = null;
      }
    };
  }, [viewingDoc, checkContentFitsViewport]);

  useEffect(() => {
    if (viewingDoc) {
      setCustomBackHandler(() => {
        setViewingDoc(null);
        return true;
      });
    } else {
      setCustomBackHandler(null);
    }
    
    return () => setCustomBackHandler(null);
  }, [viewingDoc, setCustomBackHandler]);

  // Auto-scroll to first confirmations when all docs are viewed (after closing document viewer)
  useEffect(() => {
    if (allDocsViewed && !viewingDoc && firstConfirmationsRef.current) {
      setTimeout(() => {
        firstConfirmationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [allDocsViewed, viewingDoc]);

  // Auto-scroll to second confirmations when first two are checked
  useEffect(() => {
    if (firstTwoConfirmationsChecked && !allConfirmationsChecked && secondConfirmationsRef.current) {
      setTimeout(() => {
        secondConfirmationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [firstTwoConfirmationsChecked, allConfirmationsChecked]);

  // Auto-scroll to signature section when all confirmations are checked
  useEffect(() => {
    if (allConfirmationsChecked && !hasSignature && signatureRef.current) {
      setTimeout(() => {
        signatureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [allConfirmationsChecked, hasSignature]);

  const viewDocument = (docId: string) => {
    setViewingDoc(docId);
  };

  const closeDocumentView = () => {
    setViewingDoc(null);
  };

  const markDocumentAsRead = () => {
    setDocuments(
      documents.map((doc) =>
        doc.id === viewingDoc ? { ...doc, viewed: true } : doc
      )
    );
    setViewingDoc(null);
  };

  const toggleConfirmation = (confId: string) => {
    setConfirmations(
      confirmations.map((conf) =>
        conf.id === confId ? { ...conf, checked: !conf.checked } : conf
      )
    );
  };

  // Generate PDF with all application data
  const generateApplicationPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    const navyColor: [number, number, number] = [23, 33, 52];
    const limeColor: [number, number, number] = [132, 204, 22];
    const grayColor: [number, number, number] = [100, 116, 139];

    // Signatory details for PDF
    const isCorporate = data.accountType === 'corporate';
    const signatoryName = isCorporate 
      ? data.corporateCompany?.authorizedPerson 
        ? `${data.corporateCompany.authorizedPerson.firstName} ${data.corporateCompany.authorizedPerson.lastName}`
        : 'Authorized Person'
      : data.firstName && data.lastName 
        ? `${data.firstName} ${data.lastName}` 
        : data.extractedFirstName && data.extractedLastName
          ? `${data.extractedFirstName} ${data.extractedLastName}`
          : 'Applicant';
    
    const currentDate = new Date().toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
    const currentTime = new Date().toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    // Header
    doc.setFillColor(...navyColor);
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('PayNovus', 14, 18);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Application Summary', 14, 27);
    
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 14, 27, { align: 'right' });
    
    doc.setFillColor(...limeColor);
    doc.rect(0, 35, pageWidth, 3, 'F');
    
    let yPos = 50;
    
    // Personal Information
    doc.setTextColor(...navyColor);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Personal Information', 14, yPos);
    yPos += 8;
    
    autoTable(doc, {
      startY: yPos,
      head: [],
      body: [
        ['Full Name', signatoryName],
        ['Email', data.email || 'Not provided'],
        ['Phone', data.phoneNumber ? `${data.countryCode}${data.phoneNumber}` : 'Not provided'],
        ['Date of Birth', (() => { const d = data.dateOfBirth || data.extractedDateOfBirth || ''; const p = d.split('-'); return p.length === 3 ? `${p[2].padStart(2,'0')}-${p[1].padStart(2,'0')}-${p[0]}` : d || 'Not provided'; })()],
        ['Country of Residence', data.countryOfResidence || 'Not provided'],
        ['Account Type', data.accountType.charAt(0).toUpperCase() + data.accountType.slice(1)],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: grayColor },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 14, right: 14 },
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 15;
    
    // Address
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Address', 14, yPos);
    yPos += 8;
    
    const address = data.address || data.extractedAddress;
    autoTable(doc, {
      startY: yPos,
      head: [],
      body: [
        ['Street', address?.street || 'Not provided'],
        ['City', address?.city || 'Not provided'],
        ['Postal Code', address?.postalCode || 'Not provided'],
        ['Country', address?.country || 'Not provided'],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: grayColor },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 14, right: 14 },
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 15;
    
    // Employment & Income
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Employment & Financial', 14, yPos);
    yPos += 8;
    
    autoTable(doc, {
      startY: yPos,
      head: [],
      body: [
        ['Employment Status', data.employmentStatus || 'Not provided'],
        ['Industry', data.industry || 'Not provided'],
        ['Annual Income', data.annualIncome || 'Not provided'],
        ['Source of Funds', data.sourceOfFunds?.join(', ') || 'Not provided'],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: grayColor },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 14, right: 14 },
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 15;
    
    // PEP Declaration
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('PEP Declaration', 14, yPos);
    yPos += 8;
    
    autoTable(doc, {
      startY: yPos,
      head: [],
      body: [
        ['Is PEP', data.isPEP ? 'Yes' : 'No'],
        ['PEP Details', data.pepDetails || 'N/A'],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: grayColor },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 14, right: 14 },
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 15;
    
    // Document Verification
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Document Verification', 14, yPos);
    yPos += 8;
    
    autoTable(doc, {
      startY: yPos,
      head: [],
      body: [
        ['Document Type', data.documentType || 'Not provided'],
        ['Document Number', data.extractedDocumentNumber || 'Not provided'],
        ['Document Expiry', (() => { const d = data.extractedDocumentExpiry || ''; if (d === 'NO_EXPIRY') return 'No Expiry'; const p = d.split('-'); return p.length === 3 ? `${p[2].padStart(2,'0')}-${p[1].padStart(2,'0')}-${p[0]}` : d || 'Not provided'; })()],
        ['Issuing Country', resolveCountryName(data.extractedDocumentCountry) || 'Not provided'],
        ['Nationality', resolveCountryName(data.extractedNationality) || 'Not provided'],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: grayColor },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 14, right: 14 },
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 15;
    
    // Payment Destinations
    if (data.paymentDestinations && data.paymentDestinations.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Destinations', 14, yPos);
      yPos += 8;
      
      autoTable(doc, {
        startY: yPos,
        head: [],
        body: [
          ['Countries', data.paymentDestinations.map(p => p.name).join(', ')],
        ],
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50, textColor: grayColor },
          1: { cellWidth: 'auto' }
        },
        margin: { left: 14, right: 14 },
      });
      
      yPos = (doc as any).lastAutoTable.finalY + 15;
    }
    
    // Signature Section
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Signature', 14, yPos);
    yPos += 8;
    
    autoTable(doc, {
      startY: yPos,
      head: [],
      body: [
        ['Signatory', signatoryName],
        ['Date', currentDate],
        ['Time', currentTime],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: grayColor },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 14, right: 14 },
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 10;
    
    // Add signature image if available
    if (signature) {
      try {
        doc.addImage(signature, 'PNG', 14, yPos, 60, 30);
        yPos += 35;
      } catch (e) {
        console.error('Error adding signature to PDF:', e);
      }
    }
    
    // Footer
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.getHeight();
      
      doc.setDrawColor(...grayColor);
      doc.line(14, pageHeight - 15, pageWidth - 14, pageHeight - 15);
      
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text('PayNovus - Confidential Application', 14, pageHeight - 8);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
    }
    
    return doc;
  };

  const handleSign = () => {
    setSigning(true);
    setTimeout(() => {
      updateData({
        documentsSignedAt: new Date().toISOString(),
        signedDocuments: documents.map((d) => d.id),
        documentSignature: signature || undefined,
      });
      goToNextStep();
    }, 1500);
  };

  if (viewingDoc) {
    const doc = documents.find((d) => d.id === viewingDoc);
    return (
      <div className="fixed inset-0 z-[60] bg-background flex flex-col animate-fade-in">
        <div className="bg-background/95 backdrop-blur-lg border-b border-border shrink-0 safe-area-inset-top">
          <div className="flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4">
            <button
              onClick={closeDocumentView}
              className="flex items-center gap-1.5 sm:gap-2 text-foreground hover:text-accent active:text-accent/70 transition-colors p-2 -ml-2 touch-manipulation"
              style={{ minHeight: '44px' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Back</span>
            </button>
            <img src={paynovusLogo} alt="PayNovus" className="h-6 sm:h-8 object-contain" />
          </div>
        </div>

        <div 
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 sm:py-6"
        >
          <div className="max-w-md mx-auto pb-24 animate-fade-in">
            {/* Document Header with Logo */}
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                {doc?.icon && <doc.icon className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />}
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-foreground truncate">{doc?.title}</h1>
                <p className="text-xs text-muted-foreground">PayNovus AD</p>
              </div>
            </div>
            
            {doc?.id === 'fees' ? (
              <FeesDocumentContent accountType={data.accountType} />
            ) : doc?.id === 'terms' ? (
              <TermsDocumentContent />
            ) : doc?.id === 'gtc-cards' ? (
              <CardGTCDocumentContent />
            ) : doc?.id === 'fee-doc' ? (
              <CardTermsDocumentContent accountType={data.accountType} />
            ) : doc?.id === 'info-sheet' ? (
              <InfoSheetDocumentContent onNavigateToDocument={(docId) => setViewingDoc(docId)} accountType={data.accountType} />
            ) : doc?.id === 'privacy-policy' ? (
              <PrivacyPolicyDocumentContent />
            ) : (
              <div className="prose prose-invert text-muted-foreground text-sm space-y-4">
                <p>
                  This is a simulated document preview for <strong>{doc?.title}</strong>.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
          <Button
            onClick={markDocumentAsRead}
            disabled={!hasScrolledCurrentDoc}
            className={`w-full h-14 text-lg font-semibold rounded-xl transition-all ${
              hasScrolledCurrentDoc 
                ? 'bg-accent hover:bg-accent/90 text-accent-foreground' 
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            I have read this document
          </Button>
          {!hasScrolledCurrentDoc && (
            <p className="text-sm text-muted-foreground text-center mt-2">
              Scroll to bottom to continue
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-container">
      <div className="onboarding-content">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
          Contractual Documentation
        </h1>
        
        <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
          Please review and sign the following documents
        </p>

        <div className="bg-muted/20 rounded-xl border border-border mb-4 sm:mb-6">
          {documents.map((doc, index) => {
            const Icon = doc.icon;
            return (
              <button
                key={doc.id}
                onClick={() => viewDocument(doc.id)}
                className={`w-full p-3 sm:p-4 flex items-center gap-3 sm:gap-4 hover:bg-muted/30 active:bg-muted/40 transition-colors touch-manipulation ${
                  index !== documents.length - 1 ? 'border-b border-border' : ''
                }`}
                style={{ minHeight: '56px' }}
              >
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  doc.viewed ? 'bg-accent/20' : 'bg-muted/30'
                }`}>
                  {doc.viewed ? (
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                  ) : (
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  )}
                </div>
                <span className="flex-1 text-left font-medium text-foreground text-sm sm:text-base">{doc.title}</span>
                <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              </button>
            );
          })}
        </div>

        <div ref={firstConfirmationsRef} className="mb-4 sm:mb-6">
          <p className="text-xs sm:text-sm font-medium text-foreground mb-3 sm:mb-4">
            By pressing 'Sign':
          </p>
          
          <div className="bg-muted/20 rounded-xl border border-border p-3 sm:p-4 space-y-3 sm:space-y-4 mb-4 sm:mb-6">
            {confirmations.slice(0, 2).map((conf) => (
              <button
                key={conf.id}
                onClick={() => toggleConfirmation(conf.id)}
                disabled={!allDocsViewed}
                className={`w-full flex items-start gap-3 text-left touch-manipulation ${!allDocsViewed ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ minHeight: '44px' }}
              >
                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  conf.checked ? 'bg-accent' : 'bg-muted/30 border border-border'
                }`}>
                  {conf.checked && <Check className="w-3 h-3 sm:w-4 sm:h-4 text-accent-foreground" />}
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{conf.description}</p>
              </button>
            ))}
          </div>

          <div ref={secondConfirmationsRef}>
            <p className="text-xs sm:text-sm font-medium text-foreground mb-3 sm:mb-4">
              Regarding the electronic signature, by pressing "Sign":
            </p>
            
            <div className="bg-muted/20 rounded-xl border border-border p-3 sm:p-4 space-y-3 sm:space-y-4">
              {confirmations.slice(2).map((conf) => (
                <button
                  key={conf.id}
                  onClick={() => toggleConfirmation(conf.id)}
                  disabled={!firstTwoConfirmationsChecked}
                  className={`w-full flex items-start gap-3 text-left touch-manipulation ${!firstTwoConfirmationsChecked ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ minHeight: '44px' }}
                >
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    conf.checked ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {conf.checked && <Check className="w-3 h-3 sm:w-4 sm:h-4 text-accent-foreground" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-foreground mb-0.5 sm:mb-1">{conf.title}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{conf.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Signature Section - styled like PEP Declaration */}
        <div ref={signatureRef} className={`bg-muted/50 rounded-xl p-4 mb-6 border border-border ${!allConfirmationsChecked ? 'opacity-50' : ''}`}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <PenLine className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">Electronic Signature</p>
                <p className="text-xs text-muted-foreground">
                  {allConfirmationsChecked 
                    ? 'Please sign below using your finger or mouse'
                    : 'Complete all confirmations above to unlock signature'}
                </p>
              </div>
            </div>
          </div>
          {allConfirmationsChecked ? (
            <SignaturePad onSignatureChange={setSignature} />
          ) : (
            <div className="h-32 bg-muted/30 rounded-lg flex items-center justify-center">
              <p className="text-sm text-muted-foreground">Signature pad locked</p>
            </div>
          )}
        </div>
      </div>

      <div className="onboarding-fixed-button">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleSign}
            disabled={!canSign || signing}
            className={`w-full h-12 sm:h-14 text-base sm:text-lg font-semibold rounded-full transition-all ${
              canSign 
                ? 'bg-accent hover:bg-accent/90 active:bg-accent/80 text-accent-foreground' 
                : 'bg-muted text-muted-foreground opacity-50'
            }`}
          >
            {signing ? (
              <span className="flex items-center gap-3">
                <BrandedSpinner size="sm" />
                <span>Signing...</span>
              </span>
            ) : (
              'Sign'
            )}
          </Button>
          
          {!canSign && (
            <p className="text-xs sm:text-sm text-muted-foreground text-center mt-2 sm:mt-3">
              {!allDocsViewed 
                ? 'Please review all documents before signing'
                : !allConfirmationsChecked
                  ? 'Please accept all confirmations to continue'
                  : 'Please provide your signature'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Document Content Components (extracted for cleaner code)
function FeesDocumentContent({ accountType }: { accountType?: string }) {
  return (
    <div className="text-muted-foreground text-sm space-y-6">
      {/* Header Badge */}
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
        <span className="text-xs font-medium text-accent">Tariff Document</span>
      </div>
      
      <p className="text-sm text-muted-foreground leading-relaxed">
        Tariff for fees and commissions for providing payment services to {accountType === 'corporate' ? 'Business clients' : 'Individual clients'} (EEA) at <span className="text-foreground font-medium">PayNovus AD</span>
      </p>
      
      {/* Standard Account Section */}
      <div className="bg-gradient-to-br from-accent/5 to-transparent rounded-xl border border-accent/20 p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Standard Account</h2>
            <p className="text-xs text-muted-foreground">{accountType === 'corporate' ? 'Business Account' : 'Personal Account'} Tariff</p>
          </div>
        </div>
        
        {/* Payment Account Services */}
        <div className="mb-4">
          <h3 className="text-foreground font-medium mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-accent rounded-full" />
            Payment Account Services
          </h3>
          <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Account setup fee</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Account opening fee</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Account closing fee</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Monthly Maintenance fee</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Annual fee for holding EUR, if amount exceeds 50.000</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">N/A</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Online payment system registration fee</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
            </div>
          </div>
        </div>
        
        {/* Outgoing Transfers */}
        <div className="mb-4">
          <h3 className="text-foreground font-medium mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-accent rounded-full" />
            Outgoing Transfers
          </h3>
          <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>SEPA outgoing transfers (next day)</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>SEPA outgoing transfers (same day)</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>SWIFT outgoing transfers spot (2 business days)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">35.00 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>SWIFT outgoing transfers (next day)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">35.00 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>SWIFT outgoing transfers (same day)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">35.00 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>FASTER outgoing transfers (same day)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">1.00 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>FX Rate</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">1.50%</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Local Payment Fee</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">3.50 EUR</span></div>
            </div>
          </div>
        </div>
        
        {/* Incoming Transfers */}
        <div className="mb-4">
          <h3 className="text-foreground font-medium mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-accent rounded-full" />
            Incoming Transfers
          </h3>
          <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>SEPA incoming transfers</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>SWIFT incoming transfers</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">35.00 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>FASTER incoming transfers</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">1.00 EUR</span></div>
            </div>
          </div>
        </div>
        
        {/* Internal Transfers */}
        <div className="mb-4">
          <h3 className="text-foreground font-medium mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-accent rounded-full" />
            Internal transfers
          </h3>
          <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Internal transfers between own accounts</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Internal transfers between different accounts</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
            </div>
          </div>
        </div>
        
        {/* Debit Cards */}
        <div className="mb-2">
          <h3 className="text-foreground font-medium mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-accent rounded-full" />
            Debit Cards
          </h3>
          <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Card Issuing Fee (Physical)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">5.99 EUR<span className="text-accent">*</span></span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Virtual Card Issuing</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground/70 flex items-center gap-1">
          <span className="text-accent">*</span>Applies on Issuance from second card onwards
        </p>
      </div>
      
      {/* General Provisions */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <h3 className="text-foreground font-medium mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-accent" />
          General Provisions
        </h3>
        <ol className="space-y-3 text-sm">
          {[
            'The fees and commissions stipulated in the present Tariff are valid unless otherwise arranged. Specific services not included are subject to additional arrangement.',
            'All fees and commissions shall be collected in EUR or the equivalent in another currency calculated based on fixed rate of BNB on the day of transaction.',
            'PAYNOVUS AD is entitled to debit any other account of the client with the accrued fees and commissions, even in the equivalent in other currency, if there is no sufficient cover on the respective account.',
            'All additional expenses related to the execution of the orders, including those of Bank partners, are collected in addition to the items specified in the present Tariff.',
            'PAYNOVUS AD reserves the right to apply additional charges for specific instructions or requirements, leading to extra work, unusual complexity and/or liability.',
            'The present Tariff will be applied immediately for all new Clients. For the existing clients all proposed changes in the Tariff shall become effective from 07.06.2025.'
          ].map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-accent font-semibold shrink-0">{i + 1}.</span>
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ol>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground/60">
          Tariff for fees and commissions (EEA) Standard
        </p>
        <p className="text-xs text-accent">
          Doc PN 5200-V1 | 08.01.26
        </p>
      </div>
    </div>
  );
}

function TermsDocumentContent() {
  return (
    <div className="text-muted-foreground text-sm space-y-6">
      {/* Header Badge */}
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
        <span className="text-xs font-medium text-accent">Legal Document</span>
      </div>
      
      <p className="text-sm text-muted-foreground leading-relaxed">
        General Terms and Conditions for the Opening, Keeping and Closing of Payment Accounts and the Provision of Payment Services by <span className="text-foreground font-medium">PayNovus AD</span>, Adopted by the Board of Directors on <span className="text-accent font-medium">09.06.2025</span>, Effective from the same date.
      </p>
      
      {/* Section I - General Provisions */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">I</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">General Provisions</h2>
        </div>
        <p className="mb-4 leading-relaxed">
          These General Terms and Conditions regulate the terms and conditions under which <span className="text-foreground font-medium">PayNovus AD</span>, EIK 121554961, with headquarters in the city of Sofia, 2 Positano Square, "Perform Business Center", 3rd floor, provides payment services to individuals and legal entities. PayNovus operates as an electronic money institution with a license issued by the Board of the <span className="text-foreground">Bulgarian National Bank (BNB)</span> Decision No. 73 dd. 21.07.2011.
        </p>
        <p className="mb-3 text-foreground font-medium">The payment services which PayNovus is allowed to provide include:</p>
        <div className="space-y-2">
          {[
            'Performance of payment operations, including the transfer of funds to user\'s payment account',
            'Performance of direct debits, including single direct debits',
            'Processing of payment transactions through payment cards or other similar instruments',
            'Performance of credit remittances, including orders for periodic transfers',
            'Issuance of payment instruments and/or acceptance of payments made with payment instruments'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section II - Opening of Payment Accounts */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">II</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Opening of Payment Accounts</h2>
        </div>
        <p className="mb-4 leading-relaxed">
          A payment account is an account kept in the name of one or more users of payment services, used for the storage of funds or the processing of payment transactions. Each account is identified by an individual <span className="text-accent font-medium">IBAN</span> number issued in accordance with the requirements of BNB Regulation No. 13.
        </p>
        <h3 className="text-foreground font-medium mb-3">For Individuals:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Valid ID document',
            'Full name, date and place of birth',
            'Official personal identification number or unique identifier',
            'Citizenship and address',
            'Professional activity data and purpose of business relationship'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <h3 className="text-foreground font-medium mb-3">For Legal Entities:</h3>
        <div className="space-y-2">
          {[
            'Document certifying company registration and good standing',
            'Company\'s legal and organizational format',
            'Registered office, management and correspondence address',
            'Company\'s individual identification number',
            'Details of persons with representative powers and equity owners'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section III - Online Payment System */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">III</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Payment Instruments & Online Payment System</h2>
        </div>
        <p className="mb-4 leading-relaxed">
          PayNovus provides <span className="text-accent font-medium">24/7</span> online payment system access at <span className="text-foreground font-medium">www.paynovus.com</span>. For strong customer authentication, PayNovus implements a two-factor procedure including knowledge (username/password) and possession (SMS authorization codes sent to customer's mobile).
        </p>
        <h3 className="text-foreground font-medium mb-3">Service Types:</h3>
        <div className="space-y-3">
          <div className="bg-muted/30 rounded-lg p-3 border border-border/50">
            <span className="text-accent font-medium">Online Active Access:</span>
            <span className="ml-2">Internal transfers, remittances, currency exchange, templates, free text messages</span>
          </div>
          <div className="bg-muted/30 rounded-lg p-3 border border-border/50">
            <span className="text-accent font-medium">Online Passive Access:</span>
            <span className="ml-2">Balance checking, password changes, transaction history, reference operations</span>
          </div>
        </div>
      </div>
      
      {/* Section IV - Processing of Payment Orders */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">IV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Keeping of Payment Accounts & Processing</h2>
        </div>
        <p className="mb-4 leading-relaxed">
          PayNovus shall carry out payments in observance of the Payment Services and Payment Systems Act, the Foreign Currency Act, the Law on the Measures Against Money Laundering, and BNB Ordinance № 3. Payment orders shall be processed in chronological order of their entry.
        </p>
        <h3 className="text-foreground font-medium mb-3">Transfer Types:</h3>
        <div className="space-y-2">
          {[
            { type: 'Express remittances', desc: 'Same day value date, accepted until 12:00 Bulgarian time' },
            { type: 'Tom remittances', desc: 'Next day value date, accepted until 16:00 Bulgarian time' },
            { type: 'SEPA Express', desc: 'Same day value date, accepted until 16:00 Bulgarian time' },
            { type: 'SEPA Instant', desc: 'Available 24/7, 365 days a year' },
            { type: 'FASTER', desc: 'Accepted until 17:00 Bulgarian time' },
            { type: 'Visa Direct', desc: 'Same day value date, accepted until 16:00 Bulgarian time' }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span><span className="text-accent font-medium">{item.type}:</span> {item.desc}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section V - Irrevocability */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">V</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Irrevocability of Payment Orders</h2>
        </div>
        <p className="leading-relaxed">
          The Customer may not cancel the payment order after it has been received by PayNovus. A payment order may be revoked by the payer until the end of the working day preceding the agreed day of execution. After the expiry of this period, the order may only be canceled with mutual consent of the customer and PayNovus.
        </p>
      </div>
      
      {/* Section VI - Fees and Commissions */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VI</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Fees, Commissions and Interest</h2>
        </div>
        <p className="leading-relaxed">
          PayNovus shall not charge and pay interest on the funds deposited into the accounts. Unless agreed otherwise, the customer shall owe fees and commissions charged as per the applicable Fees and Commissions Tariff, published on <span className="text-foreground font-medium">www.paynovus.com</span>.
        </p>
      </div>
      
      {/* Section VII - Closing of Payment Account */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Closing of Payment Account</h2>
        </div>
        <p className="mb-3 text-foreground font-medium">An account may be closed:</p>
        <div className="space-y-2">
          {[
            'At Customer\'s order upon submission of an Account Closing Application',
            'Upon legal entity\'s termination of activity',
            'Following the expiry of the term for which the account was opened',
            'If zero balances have been maintained for 90 calendar days',
            'By two-month advance notice from PayNovus',
            'Unilaterally by PayNovus if holder fails to perform contractual duties'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-accent font-semibold shrink-0">{i + 1}.</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section VIII - Changes to GTC */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VIII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Changes to the General Terms and Conditions</h2>
        </div>
        <p className="leading-relaxed">
          PayNovus may revise these GTC periodically. All proposed changes shall become effective within a term not less than <span className="text-accent font-medium">2 (two) months</span> as of the date of notification. The Client has the right to terminate the agreement at any time before the changes enter into force without being liable for costs and compensation.
        </p>
      </div>
      
      {/* Section IX - Prevention and Control */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">IX</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Prevention and Control</h2>
        </div>
        <p className="leading-relaxed">
          PayNovus shall not open and keep payment accounts, or process transactions that involve unfair trade practices, money laundering, financing of terrorism, or fraudulent activities. PayNovus shall not execute customer transactions ordered in favor of persons subjected to settlement sanctions. PayNovus reserves the right to retain funds in closed or suspended accounts for up to <span className="text-accent font-medium">180 days</span> where there are ongoing compliance reviews or fraud investigations.
        </p>
      </div>
      
      {/* Section X - Methods for Information */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">X</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Methods for Information and Correspondence</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          PayNovus shall grant customers online access to statements, transaction data, and available balances. The applicable language between the parties shall be Bulgarian and English.
        </p>
        <p className="text-foreground font-medium mb-2">Contact PayNovus:</p>
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
            <span>Central office: Sofia, P.O. Box 1000, 2 Pozitano Square, "Perform Business Center", fl. 3</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
            <span>Website: <span className="text-foreground font-medium">www.paynovus.com</span></span>
          </div>
        </div>
      </div>
      
      {/* Section XI - Objections */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XI</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Objections & Resolving Disputes</h2>
        </div>
        <p className="leading-relaxed">
          Customer objections shall be submitted in writing with supporting documents. PayNovus shall inform the customer within <span className="text-accent font-medium">15 days</span> of its standpoint. The maximum timeframe for a ruling shall not exceed <span className="text-accent font-medium">35 office days</span>. Disputes may be referred to the Payment Disputes Reconciliation Committee with the Consumers Protection Commission or the competent Bulgarian court.
        </p>
      </div>
      
      {/* Section XII - Reimbursement Terms */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Reimbursement Terms</h2>
        </div>
        <p className="leading-relaxed">
          PayNovus shall rectify unauthorized or improperly performed payment transactions if the Customer, without undue delay and not later than <span className="text-accent font-medium">13 months</span> after the account has been debited, informs the Company about the transaction. Reimbursement requests must be filed within <span className="text-accent font-medium">56 days</span> of the debit date.
        </p>
      </div>
      
      {/* Section XIII - Personal Data Protection */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <Shield className="w-4 h-4 text-accent" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Personal Data Protection</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          PayNovus shall process personal data in compliance with <span className="text-foreground font-medium">Regulation 2016/679 (GDPR)</span>. Detailed information is available in the Personal Data Notification Notice on PayNovus website.
        </p>
        <p className="text-foreground font-medium mb-2">Categories of personal data collected:</p>
        <div className="grid grid-cols-2 gap-2">
          {['Name', 'Date of birth', 'Residential address', 'Utility bill information', 'Employment data', 'Transaction history'].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
              <span className="text-sm">{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XIV - Representations and Warranties */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XIV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Representations and Warranties</h2>
        </div>
        <p className="mb-3 leading-relaxed">Each Party represents and warrants that:</p>
        <div className="space-y-2">
          {[
            'It is duly organised, validly existing and in good standing under the laws of its country',
            'It has all required rights, powers and authorizations to enter into the Framework Agreement',
            'It will perform its obligations with reasonable skill and care',
            'All information provided in connection with the Agreement is true, accurate and complete'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XV - Confidentiality */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Confidentiality</h2>
        </div>
        <p className="leading-relaxed">
          Each party shall treat as confidential all Confidential Information and will not, without prior written consent, disclose or use such information except for the purposes of the Framework Agreement. Disclosure is permitted to professional advisers, employees, and agents who need to know it for performing contractual obligations.
        </p>
      </div>
      
      {/* Section XVI - Indemnification */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XVI</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Indemnification</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          The Client agrees to indemnify and hold the Company harmless from losses, claims, damages, liabilities and expenses arising out of:
        </p>
        <div className="space-y-2">
          {[
            'Client\'s failure to comply with these GTC and the Framework Agreement',
            'Any act or omission of the Client',
            'Fraudulent activity or failure to comply with applicable laws',
            'Any investigation against the Client in which the Company participates'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XVII - Additional Provisions */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XVII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Additional Provisions & Definitions</h2>
        </div>
        <p className="mb-4 leading-relaxed">
          All issues not regulated by these General Terms and Conditions shall be governed by provisions of the <span className="text-foreground font-medium">Payment Services and Payment Systems Act</span>, BNB Ordinance № 3, and other Bulgarian legislation.
        </p>
        <h3 className="text-foreground font-medium mb-3">Key Definitions:</h3>
        <div className="space-y-2 text-sm">
          {[
            { term: 'Value Date', def: 'Date on which a payment services provider has credited or debited a payment account' },
            { term: 'IBAN', def: 'Unique international bank account number identifying payment accounts' },
            { term: 'SEPA', def: 'Single Euro Payments Area - transaction standard within the EU' },
            { term: 'SEPA Instant', def: 'Sending and receiving euro transfers within seconds in the SEPA region' },
            { term: 'Working day', def: 'Each day on which PayNovus conducts activity needed for payment transactions' },
            { term: 'Force majeure', def: 'Unforeseeable or unavoidable event including natural disaster, war, or pandemic' }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-accent font-medium shrink-0">{item.term}:</span>
              <span>{item.def}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground/60">
          General Terms and Conditions (EEA)
        </p>
        <p className="text-xs text-accent">
          Doc PN-GTC-V1 | 09.06.2025
        </p>
      </div>
    </div>
  );
}

function CardGTCDocumentContent() {
  return (
    <div className="text-muted-foreground text-sm space-y-6">
      {/* Header Badge */}
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
        <span className="text-xs font-medium text-accent">Legal Document</span>
      </div>
      
      <p className="text-sm text-muted-foreground leading-relaxed">
        General Terms and Conditions of <span className="text-foreground font-medium">PayNovus AD</span> for the Issuance and Use of Debit and Prepaid Cards
      </p>
      
      {/* Section I - Subject */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">I</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Subject</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          These General Terms and Conditions constitute a framework agreement within the meaning of the Law on Payment Services and Payment Systems, regulating the relations between <span className="text-foreground font-medium">PayNovus AD</span> and the clients of the Company related to:
        </p>
        <div className="space-y-2">
          {[
            'Issuance, use, and closure of debit and prepaid cards',
            'Payment and non-payment services provided through the cards',
            'Natural persons with consumer status',
            'Persons without consumer status (including legal entities)'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          The Framework Agreement enters into force from the date of signing and remains valid until the expiry date of the card, automatically renewed for reissued cards.
        </p>
      </div>
      
      {/* Section II - Types of Cards */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">II</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Types of Payment Cards</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          Cards issued by the Company are personalized payment instruments used for identification and remote access to funds.
        </p>
        <div className="space-y-2">
          {[
            'Debit cards require holding a payment account with the Company',
            'Prepaid cards issued for electronic money within applicable legislation',
            'Cards may be physical or virtual (for online/digital wallet use)',
            'Cards bear Visa or Mastercard logos with integrated chip technology',
            'Virtual prepaid cards are limited to Card Not Present transactions',
            'Funds on prepaid cards are kept in safeguarded accounts per Art. 23 ZPUPS'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section III - Payment Operations */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">III</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Payment Operations & Authorization</h2>
        </div>
        <h3 className="text-foreground font-medium mb-3">Payment operations available:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Cash withdrawal from ATM and POS terminals',
            'Cashless payments at merchants (POS and online)',
            'Subscription-based and regular payments',
            'Contactless payments (up to EUR 50 without PIN)'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <h3 className="text-foreground font-medium mb-3">Authorization methods:</h3>
        <div className="space-y-2">
          {[
            'PIN entry for ATM/POS withdrawals and payments',
            'Card number, expiry date, and CVV2/CVC2 for online purchases',
            '3D Secure (Visa Secure/Mastercard Identity Check) for enhanced online security',
            'Contactless tap for payments up to country-specific limits'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          PIN entry required after 5 consecutive contactless payments or when cumulative value exceeds EUR 150.
        </p>
      </div>
      
      {/* Section IV - Card Issuance & PIN */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">IV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Issuance, Activation & PIN</h2>
        </div>
        <div className="space-y-2">
          {[
            'Cards issued within 5 business days from agreement signing',
            'Card validity period indicated in MM/YY format',
            'Physical cards activated by first chip transaction with PIN',
            'Virtual cards activated automatically upon issuance',
            'PIN created and changed via the client portal',
            'Three incorrect PIN entries will block the card',
            'Cards delivered by courier to specified address'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section V - Cardholder Obligations */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">V</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Cardholder Rights & Obligations</h2>
        </div>
        <h3 className="text-foreground font-medium mb-3">The Cardholder is obliged to:</h3>
        <div className="space-y-2">
          {[
            'Use the card in accordance with applicable legislation and these Terms',
            'Take all reasonable steps to protect the card and personalized security features',
            'Not disclose PIN or security codes to any third party',
            'Notify the Company immediately upon learning of loss, theft, or unauthorized use',
            'Monitor account statements and report discrepancies promptly',
            'Provide accurate and up-to-date contact information',
            'Keep mobile number and email current for 3D Secure notifications'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section VI - Company Rights */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VI</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Rights of the Company</h2>
        </div>
        <p className="mb-3 leading-relaxed">The Company has the right to:</p>
        <div className="space-y-2">
          {[
            'Determine and modify transaction limits and fees per the Tariff',
            'Refuse transactions exceeding available balance or limits',
            'Block cards in cases of breach, suspected fraud, or security concerns',
            'Collect amounts due from all Cardholder accounts',
            'Request additional identification under AML procedures'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section VII - 3D Secure */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">3D Secure Authentication</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          <span className="text-foreground font-medium">Visa Secure</span> and <span className="text-foreground font-medium">Mastercard Identity Check</span> are protocols for identifying cardholders and establishing transaction authenticity.
        </p>
        <div className="space-y-2 mb-4">
          {[
            '3D static secret code: Created by cardholder via client portal',
            '3D dynamic secret code: One-time code sent via SMS or email',
            'Both codes required for online payment authorization',
            'Three incorrect entries will block 3D Secure transactions'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          SCA exemption may apply for transactions under EUR 30, cumulative under EUR 100, or fewer than 5 consecutive transactions.
        </p>
      </div>
      
      {/* Section VIII - Fees */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VIII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Fees & Currency Conversion</h2>
        </div>
        <div className="space-y-2">
          {[
            'Fees and commissions specified in the applicable Tariff',
            'Company may collect fees from any Cardholder account',
            'Currency conversion applies Company exchange rate at time of transaction',
            'Cardholder consents to automatic fee collection by signing the agreement'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section IX - Information Provision */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">IX</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Information on Transactions</h2>
        </div>
        <div className="space-y-2">
          {[
            'Transaction information available via client portal "Transactions" menu',
            'Cardholder must exercise due care in monitoring transactions',
            'Company deems Cardholder aware of transactions once accessible via any method',
            'PayNovus applies AML policies including transaction monitoring'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section X - Customer Service */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">X</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Customer Service</h2>
        </div>
        <h3 className="text-foreground font-medium mb-3">Via client portal:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Card activation, blocking, and unblocking',
            'PIN creation and modification',
            'Transaction statements and limit management',
            '3D Secure code setup and modification',
            'Two-factor authentication settings'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <p className="text-sm">
          Contact: <span className="text-accent font-medium">cards@paynovus.com</span> or via <span className="text-accent font-medium">paynovus.com</span>
        </p>
      </div>
      
      {/* Section XI - Liability */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XI</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Liability</h2>
        </div>
        <div className="space-y-2">
          {[
            'Cardholder liable for all transactions made with their card',
            'Company not liable for third-party system failures or force majeure',
            'Unauthorized transaction losses capped at EUR 50 (with exceptions)',
            'Full liability applies if Cardholder acted fraudulently or with gross negligence',
            'Company refunds unauthorized transactions within next business day upon verification',
            'Dispute notification required within 13 months of transaction'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XII - Termination */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Termination</h2>
        </div>
        <h3 className="text-foreground font-medium mb-3">Agreement may be terminated:</h3>
        <div className="space-y-2">
          {[
            'By one-month written notice from Cardholder refusing card renewal',
            'By two-month written notice from the Company',
            'Unilaterally by Company if card not activated within 3 months',
            'If no transactions made in 12 months preceding renewal date',
            'Upon death or legal incapacity of Cardholder',
            'Upon breach of agreement terms by Cardholder'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Upon termination, all cards must be returned and amounts due settled.
        </p>
      </div>
      
      {/* Section XIII - Complaints */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XIII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Complaints & Dispute Resolution</h2>
        </div>
        <div className="space-y-2 mb-4">
          {[
            'Complaints may be submitted via website, registered office, or client portal',
            'Company responds within 7 days or notifies of extended period (max 35 business days)',
            'Disputes may be referred to Conciliation Commission for Payment Disputes',
            'Commission address: Sofia 1000, 1 "Vrabcha" St., floor 4'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XIV - Personal Data */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XIV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Personal Data Protection</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          The Company processes personal data in accordance with <span className="text-foreground font-medium">Regulation (EU) 2016/679 (GDPR)</span> and the Personal Data Protection Act.
        </p>
        <div className="space-y-2">
          {[
            'Information available at paynovus.com about data processing',
            'Rights include access, correction, deletion, and portability',
            'Right to object to processing based on legitimate interest',
            'Right to withdraw consent at any time',
            'Contact Data Protection Officer for any data-related inquiries'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground/60">
          General Terms and Conditions - Cards
        </p>
        <p className="text-xs text-accent">
          Doc PN-GTC-CARDS-V1 | PayNovus AD
        </p>
      </div>
    </div>
  );
}

function CardTermsDocumentContent({ accountType }: { accountType?: string }) {
  return (
    <div className="text-muted-foreground text-sm space-y-6">
      {/* Header Badge */}
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
        <span className="text-xs font-medium text-accent">Tariff Document</span>
      </div>
      
      <p className="text-sm text-muted-foreground leading-relaxed">
        Tariff for fees and commissions for Issuing Services at <span className="text-foreground font-medium">PayNovus AD</span>
      </p>
      
      {/* VISA DEBIT Section */}
      <div className="bg-gradient-to-br from-accent/5 to-transparent rounded-xl border border-accent/20 p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">VISA DEBIT in EUR</h2>
            <p className="text-xs text-muted-foreground">{accountType === 'corporate' ? 'Business' : 'Personal'} Debit Card Tariff</p>
          </div>
        </div>
        
        {/* Non-Transactional Fees */}
        <div className="mb-4">
          <h3 className="text-foreground font-medium mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-accent rounded-full" />
            Non-Transactional Fees
          </h3>
          <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Card Issuing Fee (First card) Physical/Virtual</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Additional Virtual Card</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Additional/Replacement Physical Card</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">5.99 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Reissuing of card after expiration</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Standard Delivery Fee Europe (3-5 Business Days)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">5.99 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Standard Delivery Fee Outside of Europe (5-10 Working days)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">15.00 EUR</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Express Card Delivery Fee (next day delivery)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">DHL Cost + Standard Delivery Fee</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Monthly Card Maintenance Fee</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Change of operational limits</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
            </div>
          </div>
        </div>
        
        {/* Transactional Fees */}
        <div className="mb-2">
          <h3 className="text-foreground font-medium mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-accent rounded-full" />
            Transactional Fees
          </h3>
          <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Purchase (POS/ONLINE) - EEA</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Purchase (POS/ONLINE) - Outside EEA</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>ATM Withdrawal - EEA</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">FREE up to 500.00 EUR then 1%</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>ATM Withdrawal - Outside EEA</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">1.00 EUR + 1%</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Account Funding Transactions (Wallet top-up) - EEA</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">FREE up to 500.00 EUR then 0.25%</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Account Funding Transactions (Wallet top-up) - Non EEA</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">FREE up to 500.00 EUR then 0.5%</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Gambling transactions (deposits and payouts)</span><span className="font-medium text-accent shrink-0 whitespace-nowrap text-right">1.00% of the amount</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>PIN Change</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>PIN Change on ATM</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Balance Inquiry</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
              <div className="flex justify-between items-start gap-4 p-3 hover:bg-muted/30 transition-colors"><span>Balance Inquiry on ATM</span><span className="font-semibold text-success shrink-0 whitespace-nowrap text-right">FREE</span></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* General Provisions */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <h3 className="text-foreground font-medium mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-accent" />
          General Provisions
        </h3>
        <ol className="space-y-3 text-sm">
          {[
            'The fees and commissions stipulated in the present Tariff are valid unless otherwise arranged. Specific services not included are subject to additional arrangement.',
            'All fees and commissions shall be collected in EUR or the equivalent in another currency calculated based on fixed rate of BNB on the day of transaction.',
            'PayNovus AD is entitled to debit any other account of the client with the accrued fees and commissions, even in the equivalent in other currency, if there is no sufficient cover on the respective account.',
            'All additional expenses related to the execution of the orders, including those of Bank partners, are collected in addition to the items specified in the present Tariff.',
            'PayNovus AD reserves the right to apply additional charges for specific instructions or requirements, leading to extra work, unusual complexity and/or liability.',
            'The present Tariff will be applied immediately for all new Cardholders from 05.01.2026.'
          ].map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-accent font-semibold shrink-0">{i + 1}.</span>
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ol>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground/60">
          Tariff for fees and commissions
        </p>
        <p className="text-xs text-accent">
          Doc PN 5100-V1 | 05.01.26
        </p>
      </div>
    </div>
  );
}

function InfoSheetDocumentContent({ onNavigateToDocument, accountType }: { onNavigateToDocument: (docId: string) => void; accountType?: string }) {
  return (
    <div className="text-muted-foreground text-sm space-y-6">
      {/* Header with FID Logo */}
      <div className="flex items-start justify-between gap-4">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
          <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
          <span className="text-xs font-medium text-accent">Fee Information Document</span>
        </div>
        <img src={fidLogo} alt="Fee Information Document" className="w-12 h-12 shrink-0" />
      </div>

      {/* Provider Info */}
      <div className="bg-gradient-to-br from-accent/5 to-transparent rounded-xl border border-accent/20 p-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Name of the account provider:</span>
            <span className="text-foreground font-medium">PayNovus AD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Account name:</span>
            <span className="text-foreground font-medium">{accountType === 'corporate' ? 'Business' : 'Personal'} Payment Account</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Date:</span>
            <span className="text-foreground font-medium">01.12.2025</span>
          </div>
        </div>
      </div>
      
      <div className="space-y-2 text-sm text-muted-foreground">
        <p className="leading-relaxed flex items-start gap-2">
          <span className="text-accent mt-1">•</span>
          <span>This document informs you about the fees for using the main services linked to the payment account. It will help you to compare these fees with those of other accounts.</span>
        </p>
        <p className="leading-relaxed flex items-start gap-2">
          <span className="text-accent mt-1">•</span>
          <span>Fees may also apply for using services linked to the account which are not listed here. Full information is available in <button type="button" onClick={() => onNavigateToDocument('fees')} className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium transition-colors">{accountType === 'corporate' ? 'Standard Fee - Business Account' : 'Standard Fee - Personal Account'}</button></span>
        </p>
        <p className="leading-relaxed flex items-start gap-2">
          <span className="text-accent mt-1">•</span>
          <span>A glossary of the terms used in this document is available free of charge. It is available here: <span className="text-foreground font-medium">Glossary.pdf</span></span>
        </p>
      </div>

      {/* General Payment Account Services */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-accent" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">General Payment Account Services</h2>
        </div>
        <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
          <div className="divide-y divide-border">
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Payment Account Setup</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Payment Account Opening</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Payment Account Closing</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Payment Account Monthly Maintenance</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Online Payment System Registration Fee</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payments (Excluding Cards) */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-accent" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Payments (Excluding Cards)</h2>
        </div>
        <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
          <div className="divide-y divide-border">
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">SEPA Outgoing Credit Transfer (next day)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">SEPA Outgoing Credit Transfer (same day)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">SWIFT Outgoing (spot 2 business days)</span>
              <span className="font-medium text-accent shrink-0 ml-4">35.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">SWIFT Outgoing (next day)</span>
              <span className="font-medium text-accent shrink-0 ml-4">35.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">SWIFT Outgoing (same day)</span>
              <span className="font-medium text-accent shrink-0 ml-4">35.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">FASTER Outgoing (same day)</span>
              <span className="font-medium text-accent shrink-0 ml-4">1.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">VISA Direct (instant)</span>
              <span className="font-medium text-accent shrink-0 ml-4">3.50 EUR + 1.50% FX</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">SEPA Incoming Credit Transfer</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">SWIFT Incoming</span>
              <span className="font-medium text-accent shrink-0 ml-4">35.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">FASTER Incoming</span>
              <span className="font-medium text-accent shrink-0 ml-4">1.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Internal Transfers (own accounts)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Internal Transfers (different accounts)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">FX Rate</span>
              <span className="font-medium text-accent shrink-0 ml-4">1.50%</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Local Payment Fee</span>
              <span className="font-medium text-accent shrink-0 ml-4">3.50 EUR</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cards (Debit, Pre-paid) and Cash */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-accent" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Cards (Debit, Pre-paid) and Cash</h2>
        </div>
        <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
          <div className="divide-y divide-border">
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Issuing (Physical/Virtual)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Issuing (Pre-paid first physical card)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Additional/Replacement Physical Card</span>
              <span className="font-medium text-accent shrink-0 ml-4">5.99 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Additional Virtual Card</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Reissuing after expiration</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Standard Delivery Europe (3-5 Business Days)</span>
              <span className="font-medium text-accent shrink-0 ml-4">5.99 EUR*</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Standard Delivery Outside Europe (5-10 Days)</span>
              <span className="font-medium text-accent shrink-0 ml-4">15.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Express Card Delivery (next day)</span>
              <span className="font-medium text-accent shrink-0 ml-4">DHL Cost + Std Fee</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Monthly Maintenance (Pre-paid)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Monthly Maintenance (Debit)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Block</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Unblock</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Activation</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Deactivation</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Load (Pre-paid)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Balance Inquiry (Pre-paid)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Balance Inquiry (Debit card)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Balance Inquiry on ATM (pre-paid)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Balance Inquiry on ATM (Debit card)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Card Unjustified Disputed Transaction</span>
              <span className="font-medium text-accent shrink-0 ml-4">25.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Change of Operational Limits</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">PIN Change (Debit card)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">PIN Change on ATM (Debit card)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">PIN Change (Pre-paid card)</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">PIN Change on ATM (Pre-paid card)</span>
              <span className="font-medium text-accent shrink-0 ml-4">0.50 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Purchase (POS/ONLINE) - EEA</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Purchase (POS/ONLINE) - Outside EEA</span>
              <span className="font-semibold text-success shrink-0 ml-4">FREE</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">ATM Withdrawal (Pre-paid and Debit) - EEA</span>
              <span className="font-medium text-accent shrink-0 ml-4">2.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">ATM Withdrawal (Pre-paid and Debit) – Outside EEA</span>
              <span className="font-medium text-accent shrink-0 ml-4">3.50 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Account Funding Transactions (Wallet top-up) - EEA</span>
              <span className="font-medium text-accent shrink-0 ml-4">FREE up to 500 EUR then 0.25%</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Account Funding Transactions (Wallet top-up) – Non EEA</span>
              <span className="font-medium text-accent shrink-0 ml-4">FREE up to 500 EUR then 0.5%</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          <span className="text-accent">*</span> Fee per card delivery. Additional fees may apply for express delivery.
        </p>
      </div>

      {/* Other Services */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-accent" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Other Services</h2>
        </div>
        <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
          <div className="divide-y divide-border">
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Amendment/Cancellation/Return</span>
              <span className="font-medium text-accent shrink-0 ml-4">55.00 EUR + actual costs</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Investigation Request</span>
              <span className="font-medium text-accent shrink-0 ml-4">45.00 EUR + actual costs</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Certificate Issuance</span>
              <span className="font-medium text-accent shrink-0 ml-4">20.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Reference Issuance</span>
              <span className="font-medium text-accent shrink-0 ml-4">50.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Audit Confirmation</span>
              <span className="font-medium text-accent shrink-0 ml-4">100.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Courier Charges</span>
              <span className="font-medium text-accent shrink-0 ml-4">100.00 EUR</span>
            </div>
            <div className="flex justify-between items-center p-3 hover:bg-muted/30 transition-colors">
              <span className="text-foreground">Translation/Legalization</span>
              <span className="font-medium text-accent shrink-0 ml-4">55.00 EUR + 15.00 EUR/page</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground/60">
          Fee Information Document
        </p>
        <p className="text-xs text-accent">
          Doc PN-FID-V1 | PayNovus AD
        </p>
      </div>
    </div>
  );
}

function PrivacyPolicyDocumentContent() {
  return (
    <div className="text-muted-foreground text-sm space-y-6">
      {/* Header Badge */}
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
        <span className="text-xs font-medium text-accent">Privacy Policy</span>
      </div>
      
      <div className="bg-gradient-to-br from-accent/5 to-transparent rounded-xl border border-accent/20 p-4">
        <h2 className="text-lg font-semibold text-foreground mb-2">We Care About the Protection of Your Personal Data</h2>
        <p className="leading-relaxed">
          Whether you are a customer of PayNovus AD, an interested person or a visitor to our website, we recognize and respect the integrity of your personality.
        </p>
      </div>
      
      {/* Section I - Who We Are */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">I</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Who Are We?</h2>
        </div>
        <p className="mb-4 leading-relaxed">
          PayNovus AD is a business company listed in the Commercial Register of the Registry Agency under UIC: <span className="text-accent font-medium">121554961</span>, with seat and registered office at Sofia, Bulgaria, post code 1000, 2 Pozitano Square, "Perform Business Center", fl. 3.
        </p>
        <p className="leading-relaxed">
          Contact: tel: <span className="text-foreground">+35924373301</span>, email: <span className="text-accent">office@paynovus.com</span>. The company operates as a personal data controller in compliance with the Personal Data Protection Act.
        </p>
      </div>
      
      {/* Section II - Data Protection Officer */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">II</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Data Protection Officer</h2>
        </div>
        <p className="leading-relaxed">
          Address: Sofia, Bulgaria, post code 1000, 2 Pozitano Square, "Perform Business Center", fl. 3<br />
          e-mail: <span className="text-accent font-medium">dpo@paynovus.com</span>
        </p>
        <p className="mt-3 leading-relaxed">
          PayNovus respects the confidentiality of your personal data. The protection of your personal information throughout the entire process of personal data processing and the security of all business data is an important issue for us. We process your personal data in confidentiality and in observance of the domestic and European statutory regulations.
        </p>
      </div>
      
      {/* Section III - What is the Privacy Policy about */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">III</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">What is the Privacy Policy About?</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          The purpose of the Privacy Policy is to provide you with comprehensive information in clear and accessible language about the actions performed with your personal data, including:
        </p>
        <div className="space-y-2">
          {[
            'What type of personal data do we collect from you?',
            'What is the purpose of their collection?',
            'For what period of time is the data submitted stored?',
            'Whom do we share your data with?',
            'How do we notify you on the changes made to the Privacy Policy?',
            'What type of "cookies" do we use to improve your visit?',
            'What are your rights in terms of the personal data submitted?'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section IV - What Personal Data We Collect */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">IV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Personal Data We Collect</h2>
        </div>
        
        <h3 className="text-foreground font-medium mb-3">Personal data that identifies you:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Name, surname, family name',
            'PIN/Foreigner\'s Personal Number',
            'Address, date of birth, place of birth',
            'Identity card details, nationality',
            'Telephone number, e-mail address',
            'Current photo, biometric data',
            'Another identifier from a foreign country'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        
        <h3 className="text-foreground font-medium mb-3">Data related to the performance of transactions:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Customer\'s number, IBAN',
            'Payment orders, proceeds to a payment account',
            'Balance of payment account',
            'IP address, device ID, login metadata',
            'Cash orders to other domestic and foreign accounts'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        
        <h3 className="text-foreground font-medium mb-3">Financial state data:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Payments\' history',
            'Participation and/or ownership of company shares or securities',
            'Tax number',
            'Details of PayNovus AD financial services and products used by you',
            'Information on income not related to employment'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        
        <h3 className="text-foreground font-medium mb-3">Social and demographic information:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Related persons',
            'Employment status',
            'Gender'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        
        <h3 className="text-foreground font-medium mb-3">Data on your online conduct and preferences:</h3>
        <p className="leading-relaxed">
          IP addresses, cookies, GPS coordinates, details about your visits to our website and the applications used by you, and information on the devices from which these visits are made — this helps us find out if you are still on our site or if you are using a mobile application.
        </p>
      </div>
      
      {/* Section V - Purposes of Processing */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">V</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Purposes and Grounds for Data Processing</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          PayNovus processes your personal data in compliance with statutory duties under the <span className="text-foreground font-medium">Payment Services and Payment Systems Act</span>, the <span className="text-foreground font-medium">Law on Measures Against Money Laundering</span>, and European regulations.
        </p>
        
        <h3 className="text-foreground font-medium mb-3">Measures for your security:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Preventing fraudulent actions / crimes (identity theft, computer hacking)',
            'Guaranteeing IT security',
            'Using it in legal disputes'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        
        <h3 className="text-foreground font-medium mb-3">Data collection purposes:</h3>
        <div className="space-y-2">
          {[
            'Fulfilling duties under contracts concluded with you for the provision of services',
            'Enabling you to use our products and services',
            'Informing you on everything associated with these products and services',
            'Pre-contractual relationships during application/request filing',
            'Analyzing and evaluating your needs to offer better products',
            'Granting access to our website with personalized content'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section VI - Consent */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VI</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Consent</h2>
        </div>
        <p className="leading-relaxed mb-3">
          PayNovus collects personal data following the receipt of your <span className="text-foreground font-medium">exclusive, clear, free and unambiguous consent</span> to the goals of processing. Consent is granted upon the submission of an application for a service or through your profile in our electronic banking system.
        </p>
        <p className="leading-relaxed">
          The consent provided by you may always be withdrawn either through your profile stored in our electronic banking system, or by sending us your application for consent revocation.
        </p>
      </div>
      
      {/* Section VII - AI and Data Processing */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Artificial Intelligence (AI)</h2>
        </div>
        <p className="mb-4 leading-relaxed">
          PayNovus employs artificial intelligence (AI) technologies to enhance the functionality of our services. We are committed to ensuring that all data processing activities performed by our AI systems comply fully with applicable data protection laws, including the Bulgarian Legislation and <span className="text-accent font-medium">EU General Data Protection Regulation (EU GDPR)</span>.
        </p>
        <p className="mb-4 leading-relaxed">
          The personal data processed by our AI systems will be used solely for the specific purposes outlined in this Privacy Policy and will not be used for any other purpose without your explicit consent.
        </p>
        
        <h3 className="text-foreground font-medium mb-3">Prohibited Use of AI:</h3>
        <p className="mb-3 text-xs text-muted-foreground">In compliance with GDPR, human rights protection, and international ethical standards:</p>
        <div className="space-y-2">
          {[
            'AI will not classify individuals based on social behavior or personality characteristics leading to discrimination',
            'No real-time facial recognition or biometric processing in public spaces (except for preventing serious crimes)',
            'AI will not create manipulative content such as deepfakes',
            'No mass or uncontrolled surveillance violating privacy rights',
            'No automated decisions with significant consequences without human intervention',
            'AI will not be used in law enforcement leading to discrimination',
            'No healthcare diagnosis/treatment without medical approval',
            'No automated grading without transparency or right to appeal',
            'No risk assessments or premium determination without transparency',
            'No financial market manipulation or algorithmic trading without proper regulation'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 shrink-0" />
              <span className="text-xs">{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section VIII - Chatbots */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">VIII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Virtual Assistants (Chatbots)</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          PayNovus may provide automated communication tools such as virtual assistants ("Chatbots") to improve customer service. These are designed to offer general information and support regarding our products and services.
        </p>
        <p className="mb-3 leading-relaxed">
          Personal data collected through the Chatbot is processed in accordance with this Privacy Policy. Such processing will not be used for automated decision-making that produces legal or similarly significant effects without appropriate human review.
        </p>
        <p className="leading-relaxed">
          Data obtained through the Chatbot may be retained for a limited period sufficient to resolve your request, then securely deleted or anonymized.
        </p>
      </div>
      
      {/* Section IX - How We Process Data */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">IX</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">How We Process Your Data</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          Processing of personal data includes collecting, storing, destroying, transmitting, correcting, updating, deleting, and performing all kinds of other activities with your personal data.
        </p>
        <h3 className="text-foreground font-medium mb-3">We collect data in the following manner:</h3>
        <div className="space-y-2">
          {[
            'Upon completion of applications, forms and declarations',
            'Upon sending online consent declarations from our website',
            'Upon sending consent declarations from your electronic banking profile',
            'Upon updating data at your request',
            'Upon processing information on your visits to our website',
            'Upon processing IP addresses, cookies, operational system, and browser type',
            'Upon mailing appropriate offers on PayNovus products and services'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section X - Data Storage */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">X</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Data Storage Periods</h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
            <span><span className="text-foreground font-medium">Pre-contractual relationships:</span> 1 year from submission of request for products/services</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
            <span><span className="text-foreground font-medium">Contractual relationships:</span> 6 years from the date of contractual relationship termination</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
            <span><span className="text-foreground font-medium">Legitimate interest:</span> Until expiry of the right and/or interest</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
            <span><span className="text-foreground font-medium">Online employment applications:</span> 1 year from the date on which the application was sent</span>
          </div>
        </div>
      </div>
      
      {/* Section XI - Data Sharing */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XI</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">To Whom We May Deliver Your Data</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          PayNovus shall commit itself not to deliver your personal data to third parties without your explicit consent, unless this data is needed for the performance of assumed contractual and/or pre-contractual duties.
        </p>
        <h3 className="text-foreground font-medium mb-3">We may disclose data to:</h3>
        <div className="space-y-2">
          {[
            'Companies providing hosting services related to PayNovus website',
            'Companies developing and supporting the website of PayNovus',
            'Companies supporting PayNovus business pages in the social media',
            'Public authorities, including supervisory bodies',
            'Domestic and foreign banking and financial institutions',
            'Courts, prosecution office, police, investigators',
            'Lawyers and Notaries involved in insolvency proceedings',
            'Auditors',
            'Third parties – receivables\' collectors',
            'PayNovus counterparts (IT, logistics, telecommunication companies)',
            'Other personal data controllers with adequate data protection measures'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span className="text-xs">{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XII - Automated Decision Making */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Automated Decision Making & Profiling</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          We use technical possibilities for automated decision making in accordance with Art. 22 of the Regulation to speed up delivery of online services. We shall inform you if automated processing has been offered.
        </p>
        <h3 className="text-foreground font-medium mb-3">During automated processing we shall:</h3>
        <div className="space-y-2 mb-4">
          {[
            'Use appropriate profiling procedures',
            'Apply relevant technical and organizational measures',
            'Guarantee that factors leading to inaccuracies shall be remedied',
            'Protect your data against discrimination based on race, ethnicity, political views, religion, or any similar effect'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <h3 className="text-foreground font-medium mb-3">Your rights related to automated decision making:</h3>
        <div className="space-y-2">
          {[
            'Right to request human intervention in decision making',
            'Right to express your opinion on automated decisions',
            'Right to get automated decisions explained',
            'Right to appeal decisions taken by automated processing'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XIII - Your Rights */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XIII</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Your Rights Under GDPR</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          Pursuant to the Bulgarian and European legislation, including EU Council Regulation № 2016/679 (General Data Protection Regulation – GDPR), you may exercise the following rights:
        </p>
        <div className="space-y-2">
          {[
            'Right to access and obtain a copy of the personal data processed for you',
            'Right to ask PayNovus to correct your personal data should you establish any inaccuracies',
            'Right to ask for your personal data to be blocked or have its processing restricted',
            'Right to ask for your personal data to be obliterated (deleted), including Chatbot communication data',
            'Right to file an application for the transfer of your personal data into a structured, machine-readable format',
            'Right to submit a complaint or request for protection of your rights before the Personal Data Protection Committee'
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Section XIV - Obligation to Provide Data */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XIV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Are You Obliged to Provide Your Data?</h2>
        </div>
        <p className="mb-3 leading-relaxed">
          In order for us to provide our products and services, and in view of established contractual relationships, we shall collect and process your personal data. We shall also process your data so that PayNovus could fulfill its statutory duties and in the event of a legal (legitimate) interest.
        </p>
        <p className="leading-relaxed text-foreground font-medium">
          Should you refuse to voluntarily provide us with your personal data, PayNovus shall not be able to offer its products and services, and accordingly, shall not enter into a contract with you or shall not proceed with the performance of a contract that has already been concluded.
        </p>
      </div>
      
      {/* Section XV - Contact & Changes */}
      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">XV</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Contact & Changes to Privacy Policy</h2>
        </div>
        <p className="leading-relaxed mb-3">
          You can exercise all rights at any given time during the processing of your personal data by sending a written application, signed by you, to:
        </p>
        <p className="text-foreground font-medium mb-3">
          PayNovus AD, Sofia, Bulgaria, post code 1000, 2 Pozitano Square, "Perform Business Center", fl. 3
        </p>
        <p className="leading-relaxed mb-3">
          Or by email: <span className="text-accent font-medium">dpo@paynovus.com</span>
        </p>
        <p className="text-xs text-muted-foreground border-t border-border pt-3 mt-3">
          All changes made to this Privacy Policy shall be notified through publication on PayNovus website: <span className="text-accent">https://www.paynovus.com/</span>
        </p>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground/60">
          Privacy Policy (GDPR Compliant)
        </p>
        <p className="text-xs text-accent">
          Doc PN-PP-V1 | PayNovus AD
        </p>
      </div>
    </div>
  );
}
